import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { useGetMyOrdersQuery } from "@/features/api/paymentApi";
import { useGetCourseProgressQuery } from "@/features/api/courseApi";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";

import { BookOpen, CheckCircle, PlayCircle } from "lucide-react";

 const MyCoursesPage = () => {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth?.user);

  /* ---------------- AUTH GUARD ---------------- */
  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  /* ---------------- FETCH ORDERS ---------------- */
  const { data, isLoading } = useGetMyOrdersQuery(undefined, {
    skip: !user,
  });

  /* ---------------- FILTER SUCCESSFUL COURSE PURCHASES ---------------- */
  const purchasedCourses = useMemo(() => {
    if (!Array.isArray(data?.data)) return [];

    return data.data.filter(
      (o) =>
        o.status === "SUCCESS" &&
        o.productType === "COURSE" &&   // ✅ CORRECT CASE
        o.courseId?._id
    );
  }, [data]);

  /* ---------------- LOADING STATE ---------------- */
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 p-10">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-72 rounded-xl" />
        ))}
      </div>
    );
  }

  /* ---------------- EMPTY STATE ---------------- */
  if (!purchasedCourses.length) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <h2 className="text-3xl font-semibold mb-3">
          You haven't purchased any courses yet 🎓
        </h2>
        <Button size="lg" onClick={() => navigate("/courses")}>
          Browse Courses
        </Button>
      </div>
    );
  }

  /* ---------------- PAGE UI ---------------- */
  return (
    <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold text-center mb-10">
          My Learning
        </h1>

        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {purchasedCourses.map((order) => (
            <StudentCourseCard
              key={order.courseId._id}
              course={order.courseId}
              navigate={navigate}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/* =====================================================
   STUDENT COURSE CARD
===================================================== */
function StudentCourseCard({ course, navigate }) {
  /* -------- SAFE PROGRESS QUERY -------- */
  const {
    data: progress,
    isLoading: progressLoading,
  } = useGetCourseProgressQuery(course?._id, {
    skip: !course?._id,
  });

  const percentage = progress?.percentage ?? 0;

  let status = "Not Started";
  if (percentage === 100) status = "Completed";
  else if (percentage > 0) status = "In Progress";

  let buttonText = "Start Course";
  if (percentage > 0 && percentage < 100) buttonText = "Resume Learning";
  if (percentage === 100) buttonText = "Review Course";

  return (
    <Card className="rounded-xl border hover:shadow-xl transition flex flex-col">

      {/* -------- IMAGE SECTION -------- */}
      <div className="relative">
        {percentage === 100 && (
          <div className="absolute top-3 right-3 bg-green-600 text-white text-xs px-3 py-1 rounded-full flex items-center gap-1">
            <CheckCircle className="h-3 w-3" />
            Completed
          </div>
        )}

        {course.featuredImage ? (
          <img
            src={
              course.featuredImage.startsWith("/uploads")
                ? `https://fetc.in${course.featuredImage}`
                : course.featuredImage
            }
            alt={course.title}
            className="h-44 w-full object-cover"
          />
        ) : (
          <div className="h-44 flex items-center justify-center bg-muted">
            <PlayCircle className="h-10 w-10 text-gray-400" />
          </div>
        )}
      </div>

      {/* -------- CONTENT -------- */}
      <CardContent className="flex flex-col gap-4 p-5 flex-1">
        <h2 className="font-semibold line-clamp-2">
          {course.title}
        </h2>

        {progressLoading ? (
          <Skeleton className="h-4 w-24" />
        ) : (
          <span className="text-xs text-muted-foreground">
            {status}
          </span>
        )}

        <div>
          <div className="flex justify-between text-xs text-muted-foreground mb-1">
            <span>Progress</span>
            <span>{percentage}%</span>
          </div>
          <Progress value={percentage} />
        </div>

        <Button
          onClick={() => navigate(`/courses/${course.slug}/learn`)}
          className="w-full mt-auto"
        >
          <BookOpen className="mr-2 h-4 w-4" />
          {buttonText}
        </Button>
      </CardContent>
    </Card>
  );
}

export default MyCoursesPage;